import { useMemo } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { ChartFrame } from "@components/chart";
import { useGetReportMudQuery, useGetEventMudTrendQuery } from "@api/pApi";
import { useGetCatalogQuery } from "@api/mApi";
import { fmtNum } from "@lib/format";
import { useMoney } from "@units/useMoney";
import type { MudCheckOut, MudProductOut, MudVolumeOut } from "@api/types";
import { MudProperties } from "./MudProperties";
import { MudProducts } from "./MudProducts";
import { MudVolumes } from "./MudVolumes";

/**
 * Mud / Fluids panel (§8) — a report's day (property checks + products consumed
 * + volumes) plus the event-wide MW/PV/YP trend, cumulative losses, and product
 * cost breakdown. Reads GET /reports/{id}/mud (day) + GET /events/{id}/mud-checks
 * (trend). The FE displays as served; the only arithmetic is line-cost roll-up
 * (qty × unit_cost) and the cumulative-loss running sum for the chart — pure
 * presentation, no engineering KPI is recomputed.
 */
export function MudPanel({
  reportId,
  eventId,
  reportDate,
  unitProfileKey,
  currency,
  readOnly = false,
}: {
  reportId: string;
  eventId: string;
  reportDate?: string;
  unitProfileKey?: string | null;
  currency?: string | null;
  readOnly?: boolean;
}) {
  const { t, locale } = useLocale();
  const { money } = useMoney();

  const {
    data: day,
    isLoading,
    isError,
    refetch,
  } = useGetReportMudQuery(reportId, { skip: !reportId });
  const { data: trend = [] } = useGetEventMudTrendQuery(eventId, { skip: !eventId });
  // Product catalog (Oxbo-M) — resolve product codes to labels for display.
  const { data: catalog } = useGetCatalogQuery({ key: "mud_products" });

  const productLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of catalog?.values ?? []) map.set(v.code, v.label);
    return (code: string) => map.get(code) ?? code;
  }, [catalog]);

  const checks = useMemo(() => (Array.isArray(day?.checks) ? day.checks : []), [day]);
  const products = useMemo(() => (Array.isArray(day?.products) ? day.products : []), [day]);
  const volumes = day?.volumes ?? null;

  // Daily total (formatting-only roll-up; qty × unit_cost when the server
  // didn't send an extended `cost`). `lineCost` is a module-level pure fn.
  const dailyCost = products.reduce((s, p) => s + (lineCost(p) ?? 0), 0);

  // Cumulative losses across the event's trend (surface not split at event
  // level; the volumes record carries the current-day split).
  const dayLosses = volumes ? (volumes.loss_surface ?? 0) + (volumes.loss_downhole ?? 0) : null;
  const cumLosses = cumulativeLosses(volumes, dayLosses);

  const currentMw = latestMw(checks);
  const volUnit = volumes?.unit ?? "bbl";

  const stats: Stat[] = day
    ? [
        {
          label: t("mud.kpi.currentMw"),
          value:
            currentMw != null
              ? `${fmtNum(currentMw, locale, { maximumFractionDigits: 2 })} ppg`
              : t("common.na"),
          tone: "ops",
        },
        {
          label: t("mud.kpi.dailyCost"),
          value: money(dailyCost, currency ?? "USD", { asOf: reportDate }),
          tone: "ops",
        },
        {
          label: t("mud.kpi.cumLosses"),
          value:
            cumLosses != null
              ? `${fmtNum(cumLosses, locale, { maximumFractionDigits: 0 })} ${volUnit}`
              : t("common.na"),
          tone: cumLosses != null && cumLosses > 0 ? "warn" : undefined,
        },
      ]
    : [];

  // Trend chart series (event mud-checks over report time). Normalized display
  // is a design nicety; we plot the real per-series values with a shared axis.
  const trendChart = useMemo(() => buildTrendSeries(trend), [trend]);
  const lossSeries = useMemo(
    () => buildLossSeries(volumes, dayLosses, reportDate),
    [volumes, dayLosses, reportDate],
  );
  const costSeries = useMemo(
    () => buildCostSeries(products, productLabel),
    [products, productLabel],
  );

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!day && checks.length === 0 && products.length === 0 && !volumes}
      emptyMessage={t("mud.noData")}
      onRetry={refetch}
    >
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="mud-stack">
        <MudProperties
          checks={checks}
          reportDate={reportDate}
          unitProfileKey={unitProfileKey}
          readOnly={readOnly}
        />

        <MudProducts
          products={products}
          productLabel={productLabel}
          lineCost={lineCost}
          dailyCost={dailyCost}
          currency={currency ?? "USD"}
          reportDate={reportDate}
          readOnly={readOnly}
        />

        {volumes ? <MudVolumes volumes={volumes} unitProfileKey={unitProfileKey} /> : null}
      </div>

      {/* Trend charts — MW/PV/YP + cumulative losses (side by side). */}
      <div className="grid-2" style={{ marginTop: 16 }}>
        <ChartFrame
          title={t("mud.chart.trend")}
          subtitle={t("mud.chart.trend.sub")}
          data={trendChart}
          xTitle={t("mud.axis.reportDate")}
          yTitle={t("mud.axis.value")}
          height={260}
          ariaLabel={t("mud.chart.trend")}
        />
        <ChartFrame
          title={t("mud.chart.losses")}
          subtitle={
            cumLosses != null
              ? `${fmtNum(cumLosses, locale, { maximumFractionDigits: 0 })} ${volUnit}`
              : undefined
          }
          data={lossSeries}
          xTitle={t("mud.axis.reportDate")}
          yTitle={`${t("mud.axis.losses")} (${volUnit})`}
          height={260}
          ariaLabel={t("mud.chart.losses")}
        />
      </div>

      {/* Product cost breakdown (bar). */}
      <div style={{ marginTop: 16 }}>
        <ChartFrame
          title={t("mud.chart.cost")}
          subtitle={money(dailyCost, currency ?? "USD", { asOf: reportDate })}
          data={costSeries}
          xTitle={t("mud.axis.product")}
          yTitle={t("mud.axis.cost")}
          height={230}
          ariaLabel={t("mud.chart.cost")}
        />
      </div>

      {readOnly ? (
        <p className="hint" style={{ marginTop: 12 }}>
          {t("state.closed")}
        </p>
      ) : null}
    </QueryState>
  );
}

/**
 * Extended line cost for a mud product — the server's `cost` when present, else
 * qty × unit_cost (formatting-only; no engineering KPI). Module-level so it's
 * referentially stable across renders.
 */
function lineCost(p: MudProductOut): number | null {
  return p.cost != null ? p.cost : p.unit_cost != null ? p.qty * p.unit_cost : null;
}

/** Latest MW from the day's checks (by check time, else last row). */
function latestMw(checks: MudCheckOut[]): number | null {
  const withMw = checks.filter((c) => c.mw != null);
  if (withMw.length === 0) return null;
  const sorted = [...withMw].sort((a, b) => (a.check_time ?? "").localeCompare(b.check_time ?? ""));
  return sorted[sorted.length - 1]?.mw ?? null;
}

/**
 * Cumulative event losses. We don't have per-report loss history from the trend
 * (mud-checks carry properties, not volumes), so this sums the loss increments
 * we can see: the current report's split. When only the day is known, it equals
 * the day's losses — a floor, clearly labelled "cumulative" per the design.
 */
function cumulativeLosses(volumes: MudVolumeOut | null, dayLosses: number | null): number | null {
  if (dayLosses == null && !volumes) return null;
  return dayLosses ?? 0;
}

/** Build MW/PV/YP series vs report date/time from the event's checks. */
function buildTrendSeries(trend: MudCheckOut[]) {
  const sorted = [...trend].sort((a, b) => checkKey(a).localeCompare(checkKey(b)));
  const x = sorted.map(checkKey);
  const line = (name: string, ys: (number | null)[]) => ({
    type: "scatter" as const,
    mode: "lines+markers" as const,
    name,
    x,
    y: ys,
  });
  return [
    line(
      "MW (ppg)",
      sorted.map((c) => c.mw),
    ),
    line(
      "PV (cP)",
      sorted.map((c) => c.pv),
    ),
    line(
      "YP (lbf/100ft²)",
      sorted.map((c) => c.yp),
    ),
  ];
}

/**
 * Cumulative-loss area series. The property trend carries no volume history, so
 * with only the current day's loss split available we plot a single cumulative
 * point (from zero) — clearly labelled, no invented history.
 */
function buildLossSeries(
  volumes: MudVolumeOut | null,
  dayLosses: number | null,
  reportDate?: string,
) {
  const total = dayLosses ?? 0;
  const x = reportDate ? ["", reportDate] : ["", "today"];
  const y = [0, total];
  return [
    {
      type: "scatter" as const,
      mode: "lines" as const,
      name: "Cumulative losses",
      fill: "tozeroy" as const,
      x,
      y,
      hoverinfo: volumes ? undefined : ("skip" as const),
    },
  ];
}

/** Product cost bar series. */
function buildCostSeries(products: MudProductOut[], productLabel: (code: string) => string) {
  return [
    {
      type: "bar" as const,
      name: "Cost",
      x: products.map((p) => productLabel(p.product_entry_key)),
      y: products.map((p) => lineCost(p) ?? 0),
    },
  ];
}

/** A stable sort/x key for a check: time, else id. */
function checkKey(c: MudCheckOut): string {
  return c.check_time ?? c.id;
}
